import * as yup from 'yup';

import type { AdminFirmEmployeeInput } from '../../../../../../graphql';
import { RolodexRecordStatus } from '../../../../../../graphql';
import type { Rolodex_FirmDetail_FirmInvestorContactsQuery } from './graphql/__generated__';

export type Contact = NonNullable<
  NonNullable<
    NonNullable<Rolodex_FirmDetail_FirmInvestorContactsQuery['firmEmployees']>['items']
  >[number]
>;

export type FirmInvestorContactFormValues = {
  name: string;
  email: string;
  title: string;
  notes: string;
  recordStatus: RolodexRecordStatus;
};

export const STATUS_OPTIONS: { label: string; value: RolodexRecordStatus }[] = [
  { label: 'Effective', value: RolodexRecordStatus.Effective },
  { label: 'Deleted', value: RolodexRecordStatus.Deleted },
];

export const EMPTY_FORM_VALUES: FirmInvestorContactFormValues = {
  name: '',
  email: '',
  title: '',
  notes: '',
  recordStatus: RolodexRecordStatus.Effective,
};

export function getInitialValues(contact: Contact | null): FirmInvestorContactFormValues {
  if (!contact) {
    return EMPTY_FORM_VALUES;
  }
  return {
    name: contact.name,
    email: contact.email,
    title: contact.title ?? '',
    notes: contact.notes ?? '',
    recordStatus: contact.recordStatus,
  };
}

export function toAdminFirmEmployeeInput(
  values: FirmInvestorContactFormValues
): AdminFirmEmployeeInput {
  return {
    name: values.name.trim(),
    email: values.email.trim(),
    title: values.title.trim() || null,
    notes: values.notes.trim() || null,
    recordStatus: values.recordStatus,
  };
}

export function getTakenEmails(contacts: readonly Contact[], excludeContactId?: string): string[] {
  return contacts
    .filter(contact => contact.id !== excludeContactId)
    .map(contact => contact.email.trim().toLowerCase());
}

export function getContactValidationSchema(takenEmails: readonly string[]) {
  const normalizedTakenEmails = takenEmails.map(email => email.trim().toLowerCase());

  return yup.object({
    name: yup.string().trim().required('Full name is required'),
    email: yup
      .string()
      .trim()
      .email('Must be a valid email address')
      .required('Contact email is required')
      .test(
        'unique-email',
        'This email is already used by another contact at this firm',
        value => !value || !normalizedTakenEmails.includes(value.trim().toLowerCase())
      ),
    title: yup.string().trim(),
    notes: yup.string().trim(),
    recordStatus: yup
      .mixed<RolodexRecordStatus>()
      .oneOf(Object.values(RolodexRecordStatus))
      .required('A status is required'),
  });
}
